import React, { useMemo, useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { useTheme } from "@mui/material/styles";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import AddIcon from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import TableConfigurator from "../TableLayout/TableConfigurator";
import TableSelectorCell from "./TableSelectorCell";
import TableSelectModal from "./TableSelectModal";

import { defaultPasscodeTheme } from "../../../../constants/passCodeUtils";
import { setTableLayout } from "../../../../actions/projectmail";
import { defaultTableLayout } from "../../../../reducers/projectmail";

export const TableSelectorComponent = ({ tableLayout, type, translate, open, onClose, setTableLayoutFn }) => {
  const theme = useTheme();
  const s = useMemo(
    () => ({
      ...defaultPasscodeTheme(theme),
      inline: {
        display: "flex",
        maxWidth: "100%",
      },
      paper: {
        width: "850px",
        backgroundColor: "#eee",
        border: "2px solid #000",
        boxShadow: theme.shadows[5],
        p: theme.spacing(3, 4, 3),
      },
      add: {
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      cell: {
        pt: 1,
      },
      spacer: {
        flexGrow: 1,
      },
      paddingBottom: {
        pb: 2,
      },
      selectContainer: {
        maxHeight: "600px",
        overflowY: "scroll",
        "&::-webkit-scrollbar": { display: "none" },
        "&-ms-overflow-style": "none",
        scrollbarWidth: "none",
      },
      padding: {
        p: theme.spacing(0, 2),
        m: theme.spacing(0, 1),
      },
    }),
    [theme]
  );

  const [ownTableLayout, setOwnTableLayout] = useState([...tableLayout[type]]);
  const [openSelect, setOpenSelect] = useState(false);

  useEffect(() => {
    if (ownTableLayout.length === 0) setOwnTableLayout([...tableLayout[type]]);
  }, [setOwnTableLayout, ownTableLayout, tableLayout, type]);

  const onClickDelete = dItem => {
    const newArray = ownTableLayout.filter(item => item !== dItem);
    setOwnTableLayout(newArray);
  };

  const onClickUp = (item, idx) => {
    const newArray = [...ownTableLayout];
    const temp = newArray[idx - 1];
    newArray[idx] = temp;
    newArray[idx - 1] = item;
    setOwnTableLayout(newArray);
  };

  const onClickDown = (item, idx) => {
    const newArray = [...ownTableLayout];
    const temp = newArray[idx + 1];
    newArray[idx] = temp;
    newArray[idx + 1] = item;
    setOwnTableLayout(newArray);
  };

  return [
    <Modal
      key="selector"
      open={open}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClose={() => {
        onClose();
        setOwnTableLayout([]);
      }}
    >
      <Fade in={open}>
        <Box sx={s.paper}>
          <Box sx={s.selectContainer}>
            {ownTableLayout.map((item, idx) => (
              <Box sx={s.cell} key={"selectorcell " + item}>
                <TableSelectorCell
                  value={translate[TableConfigurator[item].label]}
                  onClickDelete={idx > 5 ? () => onClickDelete(item) : undefined}
                  onClickUp={idx > 6 ? () => onClickUp(item, idx) : undefined}
                  onClickDown={ownTableLayout.length !== idx + 1 && idx > 5 ? () => onClickDown(item, idx) : undefined}
                />
              </Box>
            ))}
            <Box sx={s.paddingBottom} />
          </Box>
          <Card>
            <CardContent>
              <Box sx={s.add}>
                <Tooltip title="Add Element">
                  <IconButton aria-label="add" onClick={() => setOpenSelect(true)} size="large">
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
          <Box sx={s.paddingBottom} />
          <Box sx={s.inline}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                onClose();
                setOwnTableLayout([]);
              }}
            >
              {translate.backButton}
            </Button>
            <Button
              variant="contained"
              sx={s.padding}
              onClick={() => {
                setTableLayoutFn(defaultTableLayout, type);
                onClose();
                setOwnTableLayout([]);
              }}
            >
              {translate.reset}
            </Button>
            <Box sx={s.spacer} />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setTableLayoutFn(ownTableLayout, type);
                onClose();
              }}
            >
              {translate.saveButton}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>,
    <TableSelectModal
      key="select-modal"
      onClick={item => {
        if (ownTableLayout.indexOf(item) === -1) {
          const newArray = [...ownTableLayout];
          newArray.push(item);
          setOwnTableLayout(newArray);
        } else setOwnTableLayout(ownTableLayout.filter(element => item !== element));
      }}
      open={openSelect}
      onClose={() => setOpenSelect(false)}
      ownTableLayout={ownTableLayout}
    />,
  ];
};

TableSelectorComponent.propTypes = {
  tableLayout: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  translate: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  setTableLayoutFn: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};

const mapStateToProps = state => ({
  tableLayout: state.projectmail.tableLayout,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  setTableLayoutFn: (tableLayout, type) => dispatch(setTableLayout(tableLayout, type)),
});

const TableSelector = connect(
  mapStateToProps,
  mapDispatchToProps
)(TableSelectorComponent);

export default TableSelector;
